import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth, BASE_URL } from "./useAuth";

const getAccess = () => sessionStorage.getItem("access_token");

export function useEwiChat() {
  const { api } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]); // 배열 보장
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const eventSourceRef = useRef(null);
  const messagesCache = useRef(new Map());

  const fetchSSE = useCallback(async (url, options = {}) => {
    const token = getAccess();
    return fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? token : "",
        ...options.headers,
      },
    });
  }, []);

  // ----- 세션 목록 -----
  const fetchSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get("/ewi/sessions");
      if (!data.status)
        throw new Error(data.error || "세션 목록을 불러올 수 없습니다");
      const sessionList = Array.isArray(data.data) ? data.data : [];
      setSessions(sessionList);
      return { ok: true, sessions: sessionList };
    } catch (error) {
      console.error("fetchSessions error:", error);
      setError(error.message);
      return { ok: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  // ----- 세션 이름 수정 -----
  const updateSessionName = useCallback(
    async (sessionId, sessionName) => {
      try {
        const { data } = await api.post("/ewi/sessions/update", {
          session_id: sessionId,
          session_name: sessionName,
        });
        if (!data.status)
          throw new Error(data.error || "세션 이름을 수정할 수 없습니다");
        setSessions((prev) =>
          prev.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  session_name: sessionName,
                  updated_at: new Date().toISOString(),
                }
              : s
          )
        );
        if (currentSession?.id === sessionId) {
          setCurrentSession((prev) => ({ ...prev, session_name: sessionName }));
        }
        return { ok: true };
      } catch (error) {
        console.error("updateSessionName error:", error);
        setError(error.message);
        return { ok: false, error: error.message };
      }
    },
    [api, currentSession]
  );

  // ----- 세션 삭제 -----
  const deleteSession = useCallback(
    async (sessionId) => {
      try {
        const { data } = await api.post("/ewi/sessions/destroy", {
          session_id: sessionId,
        });
        if (!data.status)
          throw new Error(data.error || "세션을 삭제할 수 없습니다");
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        if (currentSession?.id === sessionId) {
          setCurrentSession(null);
          setMessages([]);
          messagesCache.current.delete(sessionId);
        }
        return { ok: true };
      } catch (error) {
        console.error("deleteSession error:", error);
        setError(error.message);
        return { ok: false, error: error.message };
      }
    },
    [api, currentSession]
  );

  // ----- 메시지 로드 -----
  const fetchSessionMessages = useCallback(
    async (sessionId, lastCursorId = null) => {
      try {
        if (!lastCursorId && messagesCache.current.has(sessionId)) {
          const cached = messagesCache.current.get(sessionId);
          setMessages(cached || []);
          return { ok: true, messages: cached || [], hasMore: true };
        }
        setIsLoading(true);
        const params = new URLSearchParams({
          es: sessionId,
          ...(lastCursorId && { lcid: lastCursorId }),
        });
        const { data } = await api.get(`/ewi/sessions/chats?${params}`);
        if (!data.status)
          throw new Error(data.error || "메시지를 불러올 수 없습니다");
        const newMessages = Array.isArray(data.data) ? data.data : [];
        if (lastCursorId) {
          setMessages((prev) => [...newMessages.reverse(), ...prev]);
        } else {
          const reversed = newMessages.reverse().map((m) =>
            m.function_name
              ? {
                  ...m,
                  functionCalls: [
                    {
                      name: m.function_name,
                      args: m.function_args,
                      result: m.function_result,
                    },
                  ],
                }
              : m
          );
          setMessages(reversed);
          messagesCache.current.set(sessionId, reversed);
        }
        return {
          ok: true,
          messages: newMessages,
          hasMore: newMessages.length === 20,
        };
      } catch (error) {
        console.error("fetchSessionMessages error:", error);
        setError(error.message);
        return { ok: false, error: error.message };
      } finally {
        setIsLoading(false);
      }
    },
    [api]
  );

  // ----- SSE 전송 (웹검색 토글만) -----
  const sendMessage = useCallback(
    async (chatMsg, sessionId = null, options = {}) => {
      const { searchOnly = false } = options;
      try {
        setIsLoading(true);
        setError(null);

        // 사용자 메시지 즉시 표시
        const userMessage = {
          id: `temp-${Date.now()}`,
          role: "user",
          message: chatMsg,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, userMessage]);

        let aiMessageContent = "";
        let functionCalls = [];
        let newSessionId = sessionId || currentSession?.id;
        const aiMessageId = `ai-${Date.now()}`;

        // 모델 플레이스홀더
        setMessages((prev) => [
          ...prev,
          {
            id: aiMessageId,
            role: "model",
            message: "",
            created_at: new Date().toISOString(),
            isStreaming: true,
          },
        ]);

        const response = await fetchSSE(
          `${BASE_URL}/ewi/functional_connectively/chat_completions`,
          {
            method: "POST",
            body: JSON.stringify({
              chat_msg: chatMsg,
              session_id: newSessionId || "",
              search_only: !!searchOnly, // ✅ 토글 전달
            }),
          }
        );
        if (!response.ok) throw new Error(`서버 오류: ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.trim()) continue;

            if (line.startsWith("data: ")) {
              const jsonStr = line.slice(6).trim();
              if (jsonStr === "[DONE]") continue;

              let data;
              try {
                data = JSON.parse(jsonStr);
              } catch {
                continue;
              }

              if (data.expired) {
                reader.cancel();
                throw new Error("토큰이 만료되었습니다. 다시 시도해주세요.");
              }

              if (data.session_id && !currentSession) {
                newSessionId = data.session_id;
                setCurrentSession({ id: data.session_id });
              }

              if (data.type === "text" && data.text) {
                aiMessageContent += data.text;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === aiMessageId
                      ? { ...m, message: aiMessageContent }
                      : m
                  )
                );
              }

              if (data.type === "function_call") {
                functionCalls.push(data.function);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === aiMessageId
                      ? {
                          ...m,
                          functionCalls: [
                            ...(m.functionCalls || []),
                            data.function,
                          ],
                        }
                      : m
                  )
                );
              }

              if (data.type === "complete") {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === aiMessageId
                      ? { ...m, isStreaming: false, functionCalls }
                      : m
                  )
                );

                if (newSessionId) {
                  messagesCache.current.delete(newSessionId);
                  if (!sessionId) {
                    const newSession = {
                      id: newSessionId,
                      session_name: `${new Date().toLocaleDateString(
                        "ko-KR"
                      )} 대화`,
                      updated_at: new Date().toISOString(),
                      created_at: new Date().toISOString(),
                    };
                    setSessions((prev) => [newSession, ...prev]);
                    setCurrentSession(newSession);
                  }
                }

                const targetId = newSessionId || currentSession?.id;
                if (targetId) {
                  setSessions((prev) => {
                    const s = prev.find((x) => x.id === targetId);
                    if (s) {
                      const others = prev.filter((x) => x.id !== targetId);
                      return [
                        { ...s, updated_at: new Date().toISOString() },
                        ...others,
                      ];
                    }
                    return prev;
                  });
                }
              }

              if (data.type === "error") {
                throw new Error(data.message || "스트리밍 중 오류 발생");
              }
            } else if (line.startsWith("event: ")) {
              const event = line.slice(7).trim();
              if (event === "done") {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === aiMessageId ? { ...m, isStreaming: false } : m
                  )
                );
              }
            }
          }
        }

        if (buffer.trim()) {
          // 남은 버퍼 무시
        }

        return { ok: true, sessionId: newSessionId };
      } catch (error) {
        console.error("sendMessage error:", error);
        setError(error.message);
        setMessages((prev) => {
          const filtered = prev.filter((m) => !m.isStreaming);
          return [
            ...filtered,
            {
              id: `error-${Date.now()}`,
              role: "system",
              message: `오류: ${error.message}`,
              created_at: new Date().toISOString(),
              isError: true,
              retryMessage: chatMsg,
            },
          ];
        });
        return { ok: false, error: error.message };
      } finally {
        setIsLoading(false);
      }
    },
    [currentSession, fetchSSE]
  );

  const selectSession = useCallback(
    async (session) => {
      setCurrentSession(session);
      setMessages([]);
      setError(null);
      if (session?.id) await fetchSessionMessages(session.id);
    },
    [fetchSessionMessages]
  );

  const startNewChat = useCallback(() => {
    setCurrentSession(null);
    setMessages([]);
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, []);

  return {
    sessions,
    currentSession,
    messages,
    isLoading,
    error,
    fetchSessions,
    updateSessionName,
    deleteSession,
    selectSession,
    startNewChat,
    fetchSessionMessages,
    sendMessage,
  };
}
